import { useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess } from 'Deer/helpers/getUserAccess';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import { checkDisciplinaryModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsDisciplinaryActionsAdmin } from 'utils/helpers/rolesAccess';

import { DISCIPLINARY_ACTIONS_INTENT_ID_MAP } from 'constants/moduleIntentTypes';
import { isNetchexLogin } from 'constants/netChexConstants';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useDisciplinaryActionAdminReportIntentEllipsisValues } from '../hooks/useDisciplinaryActionAdminReportIntentEllipsisValues';
import {
  DisciplinaryActionsIntentType,
  setSelectedDisciplinaryActionIntent,
} from '../slice/DisciplinaryActionsIntentSlice';
import { DisciplinaryActionMetaFilter } from './DisciplinaryActionMetaFilter';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DisciplinaryActionIntentDropdown = ({
  breadcrumbs,
  showKebabMenu = true,

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  gridRef, // we may need it in the future
}: {
  gridRef?: unknown;
  breadcrumbs: IntentBreadcrumbType;
  showKebabMenu?: boolean;
}) => {
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const sideNavSelectedCohort = useSelectedView();
  const selectedCohortId = sideNavSelectedCohort?.id;
  const { viewType: OKRManageView } = useOKRManageViewType();
  const userAccess = getUserAccess();
  const ellipsisMenuOptions = useDisciplinaryActionAdminReportIntentEllipsisValues({ gridRef });

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<DisciplinaryActionsIntentType>>(() => {
    return [
      {
        label: 'Pending actions',
        id: DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].disciplinaryActions.pendingDisciplinaryActions,
            team: {
              directReports: L3_PATHS.team.directReports.disciplinaryActions.pendingDisciplinaryActions,
              allReports: L3_PATHS.team.allReports.disciplinaryActions.pendingDisciplinaryActions,
            },
            company: L3_PATHS.company.disciplinaryActions.pendingDisciplinaryActions,
          };

          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];
          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => true,
        value: {
          myData: {
            path: L3_PATHS['my-data'].disciplinaryActions.pendingDisciplinaryActions,
            isAccessible: checkDisciplinaryModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.disciplinaryActions.pendingDisciplinaryActions,
              isAccessible: checkDisciplinaryModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.disciplinaryActions.pendingDisciplinaryActions,
              isAccessible: checkDisciplinaryModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.disciplinaryActions.pendingDisciplinaryActions,
            isAccessible: () =>
              checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin,
          },
        },
        category: 'Manage',
        isDefault: true,
        hasMetaFilters: false,
      },
      {
        label: 'All disciplinary notes',
        id: DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].disciplinaryActions.allDisciplinaryActions,
            team: {
              directReports: L3_PATHS.team.directReports.disciplinaryActions.allDisciplinaryActions,
              allReports: L3_PATHS.team.allReports.disciplinaryActions.allDisciplinaryActions,
            },
            company: L3_PATHS.company.disciplinaryActions.allDisciplinaryActions,
          };

          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];
          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => true,
        value: {
          myData: {
            path: L3_PATHS['my-data'].disciplinaryActions.allDisciplinaryActions,
            isAccessible: checkDisciplinaryModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.disciplinaryActions.allDisciplinaryActions,
              isAccessible: checkDisciplinaryModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.disciplinaryActions.allDisciplinaryActions,
              isAccessible: checkDisciplinaryModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.disciplinaryActions.allDisciplinaryActions,
            isAccessible: () =>
              checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin,
          },
        },
        category: 'Manage',
        hasMetaFilters: false,
      },
      {
        label: 'Reports',
        id: DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ADMIN_REPORTS,

        onClick: () => {
          const values = {
            company: L3_PATHS.company.disciplinaryActions.adminReports,
          };

          const urlToPush = values[l1Module];
          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.disciplinaryActions.adminReports,
            isAccessible: () =>
              checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin,
          },
        },
        category: 'Configure',
        hasMetaFilters: true,
      },
      {
        label: 'Settings',
        id: DISCIPLINARY_ACTIONS_INTENT_ID_MAP.SETTINGS,

        onClick: () => {
          const values = {
            company: L3_PATHS.company.disciplinaryActions.disciplinaryActionsSettings,
          };

          const urlToPush = values[l1Module];
          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => {
          if (isNetchexLogin) {
            return (
              l1Module === 'company' &&
              (checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin)
            );
          }
          return l1Module === 'company' && checkUserIsDisciplinaryActionsAdmin();
        },
        value: {
          company: {
            path: L3_PATHS.company.disciplinaryActions.disciplinaryActionsSettings,
            isAccessible: () =>
              checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin,
          },
        },
        category: 'Configure',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohortId, OKRManageView, l1Module, teamType, history, userAccess?.isCustomGoalAdmin]);

  const selectedIntent = useMemo(() => {
    const foundIntent = intentDropdownOptions.find((option) => {
      const isTeamType = l1Module === 'team';
      if (isTeamType) {
        return pathname.includes(option.value.team?.[teamType]?.path);
      }
      return pathname.includes(option.value[l1Module]?.path);
    });
    dispatch(setSelectedDisciplinaryActionIntent(foundIntent));

    return foundIntent;
  }, [dispatch, intentDropdownOptions, l1Module, pathname, teamType]);

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      MetaFilters={<DisciplinaryActionMetaFilter cohortId={selectedCohortId} />}
      intentTitle={meshModules.disciplinaryActions.label}
      moduleName={meshModules.disciplinaryActions.value}
      isCategorized
      breadcrumbs={breadcrumbs}
      showKebabMenu={showKebabMenu}
      KebabMenu={<AgGridEllipsisMenu options={ellipsisMenuOptions} disableEllipsisMenu={false} />}
      ellipsisMenuOptions={ellipsisMenuOptions}
    />
  );
};

export { DisciplinaryActionIntentDropdown };
